import { SortType } from './sort'

export type IndexConfig<ITEM> = {
  identifier: (item: ITEM) => string
  indexes: {
    [s: string]: {
      title: string
      indexer: (item: ITEM) => { hash: string; title: string }[]
    }
  }
}

export type Index = {
  [s: string]: {
    title: string
    buckets: {
      [s: string]: {
        title: string
        ids: string[]
      }
    }
  }
}

export function index<ITEM>(items: ITEM[], { identifier, indexes }: IndexConfig<ITEM>): Index {
  const indexData: Index = {}
  const indexDataSorted: Index = {}

  for (const item of items) {
    const id = identifier(item)
    for (const [hash, index] of Object.entries(indexes)) {
      const buckets = index.indexer(item)

      for (const bucket of buckets) {
        indexData[hash] = indexData[hash] || {
          title: index.title,
          buckets: {},
        }
        const targetIndex = indexData[hash]
        targetIndex.buckets[bucket.hash] = targetIndex.buckets[bucket.hash] || {
          title: bucket.title,
          ids: [],
        }
        const targetBucket = targetIndex.buckets[bucket.hash]
        targetBucket.ids.push(id)
      }
    }
  }

  // Sort buckets A to Z
  for (const [hash, index] of Object.entries(indexData)) {
    const sortedBuckets = Object.entries(index.buckets).sort((bucketA, bucketB) => {
      if (bucketA[1].title < bucketB[1].title) {
        return -1
      }
      if (bucketA[1].title > bucketB[1].title) {
        return 1
      }
      return 0
    })

    const sortedBucketsFormatted = sortedBuckets.reduce(
      (sortedItems, items) => {
        return { ...sortedItems, [items[0]]: items[1] }
      },
      {} as {
        [s: string]: {
          title: string
          ids: string[]
        }
      },
    )

    indexDataSorted[hash] = {
      ...index,
      buckets: { ...sortedBucketsFormatted },
    }
  }

  return indexDataSorted
}

export type SearchConfig<ITEM> = {
  index: Index
  identifier: (item: ITEM) => string
  fullText: (query: string, item: ITEM) => boolean
}

export type SearchQuery = {
  filters: { [s: string]: string[] }
  sortType: SortType
  text: string
}

export type SearchResult<ITEM> = {
  list: ITEM[]
  facets: {
    [s: string]: {
      title: string
      buckets: {
        [s: string]: {
          title: string
          count: number
        }
      }
    }
  }
}

export async function search<ITEM>(
  items: ITEM[],
  config: SearchConfig<ITEM>,
  query: SearchQuery,
): Promise<SearchResult<ITEM>> {
  const list = filter(items, query)

  await new Promise((res) => setTimeout(res, 0))

  const facets: SearchResult<ITEM>['facets'] = {}
  for (const [indexHash, index] of Object.entries(config.index)) {
    facets[indexHash] = { title: index.title, buckets: {} }
    for (const [bucketHash, bucket] of Object.entries(index.buckets)) {
      const queriedBuckets = query.filters[indexHash] || []
      if (queriedBuckets.includes(bucketHash)) {
        facets[indexHash].buckets[bucketHash] = {
          title: bucket.title,
          count: list.length,
        }
      } else {
        await new Promise((res) => setTimeout(res, 0))

        let virtualBuckets: string[] = []
        virtualBuckets = virtualBuckets.concat(queriedBuckets)
        virtualBuckets.push(bucketHash)

        const virtualFilters: SearchQuery['filters'] = {
          ...query.filters,
          [indexHash]: virtualBuckets,
        }

        const virtualResult = filter(items, {
          text: query.text,
          filters: virtualFilters,
        })

        await new Promise((res) => setTimeout(res, 0))

        const count = filter(virtualResult, {
          text: query.text,
          filters: { [indexHash]: [bucketHash] },
        }).length

        facets[indexHash].buckets[bucketHash] = {
          title: bucket.title,
          count,
        }
      }
    }
  }

  return { list, facets }

  function filter(items: ITEM[], query: Omit<SearchQuery, 'sortType'>): ITEM[] {
    return items.filter((item) => {
      return matchFilters(item, query.filters) && config.fullText(query.text, item)
    })
  }

  function matchFilters(item: ITEM, filters: SearchQuery['filters']): boolean {
    if (Object.keys(filters).length === 0) {
      return true
    }

    const id = config.identifier(item)
    return Object.entries(filters).every(([index, buckets]) => matchFilter(id, index, buckets))
  }

  function matchFilter(id: string, index: string, buckets: string[]): boolean {
    // ignore index that does not exists
    if (!(index in config.index)) {
      return true
    }

    return buckets.some((bucket) => {
      const ids = config.index[index].buckets[bucket].ids
      return ids.includes(id)
    })
  }
}
